/**
 * Roster-entry ratings — coach rating of an under-13 ghost player.
 *
 * Rides on the existing `ratings` table (see migration 00085):
 *   - `ratee_id` = roster_entries.id
 *   - `ratee_type` = 'ROSTER_ENTRY'
 *   - `rater_id` = coach's auth.uid()
 *   - `is_blind` defaults to FALSE (ghost players don't self-rate;
 *     the blind state machine doesn't apply)
 *   - `state` = 'REVEALED' (coach posts directly, no reveal window)
 *
 * RLS gates writes to the team's coach; parent linked via
 * `roster_entries.parent_user_id` can SELECT.
 *
 * Identical 0-100 score semantics as PLAYER ratings — so the
 * existing composite / radar / velocity math reuses everything.
 */

export const CORNER_IDS = [
  'corner-technical',
  'corner-physical',
  'corner-psych',
  'corner-social',
] as const;

export type CornerId = (typeof CORNER_IDS)[number];

/**
 * Input for inserting a single rating row against a roster_entry.
 * The composite view typically inserts four rows in one go (one per
 * corner) — this is the shape per row.
 */
export interface RosterEntryRatingInput {
  /** roster_entries.id */
  ratee_id: string;
  corner_id: CornerId;
  /** Optional sub-corner refinement. */
  sub_corner_id?: string | null;
  /** 0–100, integer. */
  score: number;
  /** SESSION or GAME context. */
  context: 'SESSION' | 'GAME';
  session_id?: string | null;
  game_id?: string | null;
}

export type RatingValidationResult =
  | { ok: true; input: RosterEntryRatingInput }
  | { ok: false; errors: RatingValidationError[] };

export interface RatingValidationError {
  field: keyof RosterEntryRatingInput | 'context_target';
  message: string;
}

/**
 * Validate a single rating-row input before it leaves the client.
 *
 * Rules:
 *   - ratee_id: required, non-empty
 *   - corner_id: required, must be one of the four canonical corner ids
 *   - score: integer 0–100
 *   - context: 'SESSION' or 'GAME'
 *   - context target: session_id required when context=SESSION;
 *     game_id required when context=GAME (mutually exclusive — both
 *     set is also rejected)
 */
export function validateRosterEntryRating(
  input: RosterEntryRatingInput,
): RatingValidationResult {
  const errors: RatingValidationError[] = [];

  if (!input.ratee_id?.trim()) {
    errors.push({ field: 'ratee_id', message: 'Roster entry id is required.' });
  }

  if (!CORNER_IDS.includes(input.corner_id)) {
    errors.push({ field: 'corner_id', message: `corner_id must be one of: ${CORNER_IDS.join(', ')}.` });
  }

  if (!Number.isInteger(input.score)) {
    errors.push({ field: 'score', message: 'Score must be a whole number.' });
  } else if (input.score < 0 || input.score > 100) {
    errors.push({ field: 'score', message: 'Score must be between 0 and 100.' });
  }

  if (input.context !== 'SESSION' && input.context !== 'GAME') {
    errors.push({ field: 'context', message: "Context must be 'SESSION' or 'GAME'." });
  }

  // Context-target mutual exclusion. session_id only valid for SESSION,
  // game_id only valid for GAME.
  if (input.context === 'SESSION' && input.game_id) {
    errors.push({ field: 'context_target', message: 'SESSION context cannot carry a game_id.' });
  }
  if (input.context === 'GAME' && input.session_id) {
    errors.push({ field: 'context_target', message: 'GAME context cannot carry a session_id.' });
  }

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, input };
}

/**
 * Compute a simple per-corner composite from a list of rating rows
 * for a single roster_entry. Returns one number per corner — mean
 * of all scores in that corner — or null if no rows for that corner.
 *
 * The full ratingEngine composite (with bio-band adjustment +
 * psych-social floor) is a PLAYER-side concept that depends on a
 * `BioBand` row keyed off `users.id` and the player's age band. For
 * ROSTER_ENTRY we don't have BioBand rows yet (DoB is on the entry
 * itself, no maturity offset stored), so a plain mean is the
 * honest starting point. The dashboard can later upgrade to the
 * full composite once bio-band capture for roster entries lands.
 */
export interface CornerComposite {
  corner_id: CornerId;
  mean: number | null;
  count: number;
}

export function computeRosterEntryCornerComposite(
  ratings: ReadonlyArray<{ corner_id: string; score: number }>,
): CornerComposite[] {
  return CORNER_IDS.map((c) => {
    const matched = ratings.filter((r) => r.corner_id === c);
    const count = matched.length;
    if (count === 0) return { corner_id: c, mean: null, count };
    const mean = matched.reduce((sum, r) => sum + r.score, 0) / count;
    return { corner_id: c, mean, count };
  });
}
